/**
 * Bookmarkable product-editor tab + placement URLs (2026-07-15, Jake --
 * "real paths, NO #hash").
 *
 * The product editor is ONE page that swaps its ContextPanel internally by
 * `selectedLeftRailItem` (a store ref, LEFT_RAIL_ITEMS key or a
 * `placement:<id>` string). This module is the single source of truth that
 * maps that store selection to/from a bookmarkable, refresh-stable URL:
 *
 *   /distributor/product-editor/:campaignProductId/:tab
 *   /distributor/product-editor/:campaignProductId/placements/:placementId
 *
 * The `:tab` slug is a URL-friendly alias of a LEFT_RAIL_ITEMS key. The KEYS
 * are unchanged by this feature (STOREFRONT is still "storefront"); only the
 * URL slug differs -- notably STOREFRONT <-> `product-listing` so the URL
 * matches the tab's relabeled "Product Listing" display (#2091) while the
 * store key stays STOREFRONT.
 *
 * Design-philosophy: single-source state. The slug<->key map lives here once
 * so the router, the sync composable, and the last-viewed persistence never
 * disagree about which slug names which tab.
 */
import { LEFT_RAIL_ITEMS } from "@/stores/productEditorUiState";

// Ordered so the FIRST entry is the fallback landing tab (supplier-sku) when
// no last-viewed tab is stored. KEY -> URL slug.
const KEY_TO_SLUG = Object.freeze({
    [LEFT_RAIL_ITEMS.SUPPLIER_SKU]: "supplier-sku",
    [LEFT_RAIL_ITEMS.SIZES_COLORS]: "sizes-colors",
    [LEFT_RAIL_ITEMS.PRICING]: "pricing",
    // STOREFRONT key is unchanged; its bookmarkable slug matches the tab's
    // relabeled "Product Listing" display (#2091) for a clean URL.
    [LEFT_RAIL_ITEMS.STOREFRONT]: "product-listing",
});

// Reverse: URL slug -> KEY. Built from KEY_TO_SLUG so the two can never drift.
const SLUG_TO_KEY = Object.freeze(
    Object.fromEntries(
        Object.entries(KEY_TO_SLUG).map(([key, slug]) => [slug, key]),
    ),
);

// The first tab slug -- the fallback landing when there is no stored history
// and the bare-id redirect target of last resort.
export const FIRST_TAB_SLUG = KEY_TO_SLUG[LEFT_RAIL_ITEMS.SUPPLIER_SKU];

/**
 * Map a LEFT_RAIL_ITEMS key to its URL slug.
 * Returns null for an unknown key or a `placement:<id>` selection (placements
 * are not tab slugs -- callers route them via placementRouteFor instead).
 */
export function keyToSlug(key) {
    if (key == null) return null;
    return KEY_TO_SLUG[key] ?? null;
}

/**
 * Map a URL tab slug to its LEFT_RAIL_ITEMS key.
 * Returns null for an unknown / invalid slug so callers can fall back to the
 * last-viewed (or first) tab -- keeping URL and view from ever disagreeing.
 */
export function slugToKey(slug) {
    if (slug == null) return null;
    return SLUG_TO_KEY[slug] ?? null;
}

/**
 * True when `slug` names a real tab. Convenience for router/redirect guards.
 */
export function isValidTabSlug(slug) {
    return slug != null && Object.prototype.hasOwnProperty.call(SLUG_TO_KEY, slug);
}

// ---------------------------------------------------------------------------
// Placement selection <-> URL. selectedLeftRailItem carries a placement as
// the string `placement:<id>`; the URL carries it as `/placements/:id`.
// ---------------------------------------------------------------------------

const PLACEMENT_PREFIX = "placement:";

/**
 * Extract the placement id from a `placement:<id>` selection string, or null
 * when `key` is not a placement selection.
 */
export function placementIdFromKey(key) {
    if (typeof key !== "string" || !key.startsWith(PLACEMENT_PREFIX)) {
        return null;
    }
    const id = key.slice(PLACEMENT_PREFIX.length);
    return id === "" ? null : id;
}

/**
 * Build the `placement:<id>` selection string from a route placementId.
 * Returns null for an empty/nullish id.
 */
export function placementKeyFromId(placementId) {
    if (placementId == null || placementId === "") return null;
    return `${PLACEMENT_PREFIX}${placementId}`;
}

// ---------------------------------------------------------------------------
// Last-viewed tab persistence. Stored GLOBALLY (one key) -- the bare-id
// redirect reads it so re-opening the editor lands where the distributor last
// worked, falling back to the first tab when there is no history.
// ---------------------------------------------------------------------------

export const LAST_TAB_STORAGE_KEY = "tf:product-editor:last-tab";

// Guard localStorage access: it throws in private-mode Safari and is absent in
// non-browser (SSR / some test) environments. A failed read/write must never
// break navigation -- it just falls back to the first tab.
function safeStorage() {
    try {
        if (typeof window === "undefined" || !window.localStorage) return null;
        return window.localStorage;
    } catch {
        return null;
    }
}

/**
 * Persist the last-viewed tab SLUG (ignored for invalid slugs so we never
 * store garbage that the redirect would then have to re-validate).
 */
export function persistLastTabSlug(slug) {
    if (!isValidTabSlug(slug)) return;
    const store = safeStorage();
    if (!store) return;
    try {
        store.setItem(LAST_TAB_STORAGE_KEY, slug);
    } catch {
        // Quota / private-mode: silently skip -- persistence is best-effort.
    }
}

/**
 * Read the last-viewed tab slug, validated. Returns the first-tab slug when
 * nothing valid is stored so the bare-id redirect always has a real target.
 */
export function readLastTabSlug() {
    const store = safeStorage();
    if (!store) return FIRST_TAB_SLUG;
    let stored = null;
    try {
        stored = store.getItem(LAST_TAB_STORAGE_KEY);
    } catch {
        stored = null;
    }
    return isValidTabSlug(stored) ? stored : FIRST_TAB_SLUG;
}
